import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, Modal, Field, TextInput, Select,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { Truck, Plus, Trash2, IndianRupee } from "lucide-react";
import { fmtDate, fmtINR } from "@/lib/format";
import { toast } from "sonner";
import TID from "@/constants/testIds";

const EMPTY_FORM = { code: "", name: "", contact_person: "", phone: "", is_active: true };

export default function Suppliers() {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [ratesFor, setRatesFor] = useState(null); // supplier row, or null
    const canEdit = hasRole(user, "CORPORATE_ADMIN");

    const load = () =>
        api.get("/suppliers")
            .then((r) => setRows(r.data))
            .catch((e) => setError(formatApiError(e)));

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setModalOpen(true);
    };
    const openEdit = (row) => {
        setEditing(row);
        setForm({
            code: row.code, name: row.name,
            contact_person: row.contact_person || "", phone: row.phone || "",
            is_active: row.is_active,
        });
        setModalOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (editing) await api.put(`/suppliers/${editing.id}`, form);
            else await api.post("/suppliers", form);
            toast.success(editing ? "Supplier updated" : "Supplier created");
            setModalOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete supplier ${row.code}? This action cannot be undone.`)) return;
        try {
            await api.delete(`/suppliers/${row.id}`);
            toast.success("Supplier deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid={TID.suppliersPage}>
            <PageHeader
                eyebrow="FOUNDATION · MASTERS"
                title="Suppliers"
                subtitle="The master list of vendors quoting for RM inputs and finished-part supply. Referenced everywhere a supplier used to be free-typed: ZBC supplier quotes, Supplier Compare, and the Intelligent Wizard's supplier comparison."
                actions={
                    canEdit && (
                        <CopperButton onClick={openCreate} data-testid={TID.suppliersAddBtn}>
                            <Plus className="h-4 w-4" /> Add Supplier
                        </CopperButton>
                    )
                }
            />

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}

            {rows && rows.length === 0 && (
                <EmptyState icon={Truck} title="No suppliers yet" description="Add your first vendor to start selecting suppliers from a master instead of typing the name." />
            )}

            {rows && rows.length > 0 && (
                <Card>
                    <table data-testid={TID.suppliersTable} className="w-full table-rowhover">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50/60">
                                <th className="eyebrow text-left py-2 px-4">Code</th>
                                <th className="eyebrow text-left py-2 px-4">Name</th>
                                <th className="eyebrow text-left py-2 px-4">Contact</th>
                                <th className="eyebrow text-left py-2 px-4">Phone</th>
                                <th className="eyebrow text-left py-2 px-4">Status</th>
                                <th className="eyebrow text-left py-2 px-4">Created</th>
                                <th className="eyebrow text-right py-2 px-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} data-testid={TID.supplierRow(r.id)} className="border-b border-slate-100">
                                    <td className="py-1.5 px-4 text-[13px] font-mono font-semibold text-slate-900">{r.code}</td>
                                    <td className="py-1.5 px-4 text-[13px]">{r.name}</td>
                                    <td className="py-1.5 px-4 text-[13px] text-slate-600">{r.contact_person || "—"}</td>
                                    <td className="py-1.5 px-4 text-[12px] font-mono text-slate-600">{r.phone || "—"}</td>
                                    <td className="py-1.5 px-4">
                                        <Pill tone={r.is_active ? "emerald" : "slate"}>{r.is_active ? "Active" : "Inactive"}</Pill>
                                    </td>
                                    <td className="py-1.5 px-4 text-[12px] font-mono tabular-nums text-slate-500">{fmtDate(r.created_at)}</td>
                                    <td className="py-1.5 px-4 text-right">
                                        <div className="inline-flex gap-1">
                                            <SlateButton onClick={() => setRatesFor(r)} variant="ghost" className="h-7 px-2 text-xs">
                                                <IndianRupee className="h-3 w-3" /> Steel Rates
                                            </SlateButton>
                                            {canEdit && (
                                                <>
                                                    <SlateButton onClick={() => openEdit(r)} className="h-7 px-2.5 text-xs">Edit</SlateButton>
                                                    <SlateButton
                                                        onClick={() => remove(r)}
                                                        variant="ghost"
                                                        className="h-7 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                                                        title={`Delete ${r.code}`}
                                                        data-testid={`supplier-delete-${r.id}`}
                                                    >
                                                        <Trash2 className="h-3 w-3" /> Delete
                                                    </SlateButton>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}

            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                title={editing ? `Edit Supplier — ${editing.code}` : "Add New Supplier"}
                footer={
                    <>
                        <SlateButton variant="outline" onClick={() => setModalOpen(false)}>Cancel</SlateButton>
                        <CopperButton onClick={submit} disabled={submitting}>
                            {submitting ? "Saving…" : (editing ? "Save Changes" : "Create Supplier")}
                        </CopperButton>
                    </>
                }
            >
                <form onSubmit={submit} className="space-y-5">
                    <Field label="Supplier Code" required helper="Short unique code, e.g. SHRIVINAYAK">
                        <TextInput
                            value={form.code}
                            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                            placeholder="SHRIVINAYAK"
                            required
                            disabled={!!editing}
                        />
                    </Field>
                    <Field label="Supplier Name" required>
                        <TextInput
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Shrivinayak Engineering Works"
                            required
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Contact Person">
                            <TextInput value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} placeholder="Suresh Patil" />
                        </Field>
                        <Field label="Phone">
                            <TextInput value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91-98220-11223" />
                        </Field>
                    </div>
                    <Field label="Status">
                        <Select value={form.is_active ? "active" : "inactive"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "active" })}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </Select>
                    </Field>
                </form>
            </Modal>

            <SupplierRatesModal supplier={ratesFor} onClose={() => setRatesFor(null)} canEdit={canEdit} />
        </div>
    );
}

/** Requirement #3 — supplier-side steel rate, kept distinct from the
 * customer-facing SteelGrade.rate_per_kg. Nothing forces the two to match. */
function SupplierRatesModal({ supplier, onClose, canEdit }) {
    const [grades, setGrades] = useState([]);
    const [rates, setRates] = useState(null);
    const [gradeId, setGradeId] = useState("");
    const [rate, setRate] = useState("");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = () => {
        if (!supplier) return;
        Promise.all([
            api.get("/steel-grades"),
            api.get("/supplier-steel-rates", { params: { supplier_id: supplier.id } }),
        ]).then(([g, r]) => { setGrades(g.data); setRates(r.data); });
    };
    useEffect(() => { if (supplier) { load(); setGradeId(""); setRate(""); setReason(""); } }, [supplier]);

    const gradeMap = useMemo(() => Object.fromEntries(grades.map((g) => [g.id, g])), [grades]);

    const submit = async (e) => {
        e.preventDefault();
        if (!gradeId || !rate) return;
        setSubmitting(true);
        try {
            await api.post("/supplier-steel-rates", {
                supplier_id: supplier.id, grade_id: gradeId, rate_per_kg: parseFloat(rate), reason,
            });
            toast.success("Supplier steel rate saved");
            setGradeId(""); setRate(""); setReason("");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const removeRate = async (r) => {
        if (!window.confirm(`Remove ${gradeMap[r.grade_id]?.code || "this"} rate?`)) return;
        try {
            await api.delete(`/supplier-steel-rates/${r.id}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    if (!supplier) return null;

    return (
        <Modal open={!!supplier} onClose={onClose} title={`Steel Rates — ${supplier.name}`} size="lg">
            <div className="space-y-4">
                <p className="text-[12px] text-slate-500">
                    What this supplier is actually quoting per steel grade — independent of the customer-facing
                    rate in Steel Grades &amp; Scrap. Used by the Variance Breakup drill-down on ZBC Detail to
                    isolate a steel-rate driven gap from a weight or process driven one.
                </p>

                {canEdit && (
                    <form onSubmit={submit} className="flex items-end gap-2 border-b border-slate-200 pb-4">
                        <div className="flex-1">
                            <Field label="Steel Grade" required>
                                <Select value={gradeId} onChange={(e) => setGradeId(e.target.value)} required>
                                    <option value="">Select grade…</option>
                                    {grades.map((g) => <option key={g.id} value={g.id}>{g.code} — ₹{g.rate_per_kg}/kg (ours)</option>)}
                                </Select>
                            </Field>
                        </div>
                        <div className="w-32">
                            <Field label="Rate ₹/kg" required>
                                <TextInput type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="70.50" required />
                            </Field>
                        </div>
                        <div className="flex-1">
                            <Field label="Reason">
                                <TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Q3 quote" />
                            </Field>
                        </div>
                        <CopperButton type="submit" disabled={submitting} className="h-9">
                            {submitting ? "Saving…" : "Save"}
                        </CopperButton>
                    </form>
                )}

                {rates === null ? (
                    <LoadingState />
                ) : rates.length === 0 ? (
                    <div className="text-[12px] text-slate-500 py-4 text-center">No steel rates on file for this supplier yet.</div>
                ) : (
                    <table className="w-full text-[12.5px]">
                        <thead>
                            <tr className="border-b border-slate-200 text-slate-500">
                                <th className="text-left py-1.5">Grade</th>
                                <th className="text-right py-1.5">Supplier ₹/kg</th>
                                <th className="text-right py-1.5">Our ₹/kg</th>
                                <th className="text-right py-1.5">Δ</th>
                                {canEdit && <th className="text-right py-1.5"></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {rates.map((r) => {
                                const g = gradeMap[r.grade_id];
                                const delta = g ? r.rate_per_kg - g.rate_per_kg : null;
                                return (
                                    <tr key={r.id} className="border-b border-slate-100">
                                        <td className="py-1.5 font-mono font-semibold">{g?.code || r.grade_id}</td>
                                        <td className="py-1.5 text-right font-mono tabular-nums">{fmtINR(r.rate_per_kg, 2)}</td>
                                        <td className="py-1.5 text-right font-mono tabular-nums text-slate-500">{g ? fmtINR(g.rate_per_kg, 2) : "—"}</td>
                                        <td className={`py-1.5 text-right font-mono tabular-nums ${delta > 0 ? "text-red-600" : delta < 0 ? "text-emerald-600" : "text-slate-500"}`}>
                                            {delta != null ? `${delta >= 0 ? "+" : ""}${fmtINR(delta, 2)}` : "—"}
                                        </td>
                                        {canEdit && (
                                            <td className="py-1.5 text-right">
                                                <button onClick={() => removeRate(r)} className="text-red-500 hover:text-red-700" title="Remove">
                                                    <Trash2 className="h-3 w-3 inline" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </Modal>
    );
}
